import { create } from 'zustand';
import { useAuthStore } from './authStore';

export type TierType = 'FREE' | 'PREMIUM';

export interface QuotaInfo {
  used: number;
  limit: number;
  remaining: number;
}

export interface LimitsState {
  tier: TierType | null;
  sessions: QuotaInfo | null;
  suggestions: QuotaInfo | null;
  transcription: QuotaInfo | null;
  sessionsResetAt: Date | null;
  resetAt: Date | null;
  isLoading: boolean;
  error: string | null;

  // Modal state
  modalLimitReached: {
    isOpen: boolean;
    quotaType: 'suggestions' | 'transcription' | null;
  };

  // Actions
  fetchLimits: () => Promise<void>;
  decrementSuggestions: () => void;
  decrementTranscription: (minutes: number) => void;
  showLimitModal: (quotaType: 'suggestions' | 'transcription') => void;
  closeLimitModal: () => void;
  reset: () => void;
}

const initialState = {
  tier: null,
  sessions: null,
  suggestions: null,
  transcription: null,
  sessionsResetAt: null,
  resetAt: null,
  isLoading: false,
  error: null,
  modalLimitReached: {
    isOpen: false,
    quotaType: null,
  },
};

export const useLimitsStore = create<LimitsState>((set, get) => ({
  ...initialState,

  fetchLimits: async () => {
    const { tier } = get();
    // Only show loading state on initial fetch (when tier is null)
    // Prevents flickering on auto-refresh
    if (!tier) {
      set({ isLoading: true, error: null });
    } else {
      set({ error: null });
    }

    try {
      const { accessToken } = useAuthStore.getState();
      if (!accessToken) {
        throw new Error('No authentication token found');
      }

    const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
    const response = await fetch(`${apiBaseUrl}/api/limits/current`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch limits: ${response.statusText}`);
      }

      const data = await response.json();

      set({
        tier: data.tier,
        sessions: data.sessions || null,
        suggestions: data.suggestions,
        transcription: data.transcription,
        sessionsResetAt: data.sessionsResetAt ? new Date(data.sessionsResetAt) : null,
        resetAt: new Date(data.resetAt),
        isLoading: false,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      set({ error: errorMessage, isLoading: false });
      console.error('Failed to fetch limits:', error);
    }
  },

  decrementSuggestions: () => {
    const { suggestions, tier } = get();
    if (!suggestions || tier === 'PREMIUM') return;

    const newUsed = suggestions.used + 1;
    const newRemaining = Math.max(0, suggestions.remaining - 1);

    set({
      suggestions: {
        ...suggestions,
        used: newUsed,
        remaining: newRemaining,
      },
    });

    // Show warning toast at 90% usage (9/10 for suggestions)
    if (newUsed === 9 && suggestions.limit === 10) {
      console.warn('⚠️ Warning: 90% of suggestion quota used (9/10)');
      // Could trigger a toast notification here
    }
    if (newRemaining === 0) {
      void get().fetchLimits();
    }
  },

  decrementTranscription: (minutes: number) => {
    const { transcription, tier } = get();
    if (!transcription || tier === 'PREMIUM') return;

    const newUsed = transcription.used + minutes;
    const newRemaining = Math.max(0, transcription.remaining - minutes);

    set({
      transcription: {
        ...transcription,
        used: newUsed,
        remaining: newRemaining,
      },
    });

    // Show warning toast at 90% usage (27/30 for transcription)
    if (newUsed >= 27 && transcription.limit === 30 && transcription.used < 27) {
      console.warn('⚠️ Warning: 90% of transcription quota used (27/30 minutes)');
      // Could trigger a toast notification here
    }
    if (newRemaining === 0) {
      void get().fetchLimits();
    }
  },

  showLimitModal: (quotaType: 'suggestions' | 'transcription') => {
    set({
      modalLimitReached: {
        isOpen: true,
        quotaType,
      },
    });
  },

  closeLimitModal: () => {
    set({
      modalLimitReached: {
        isOpen: false,
        quotaType: null,
      },
    });
  },

  reset: () => {
    set(initialState);
  },
}));
